// SY13 화면관리 API — 2.0 /api/v1/system/screen-settings (LOGISPRD-17091, 계약 = 17091 댓글 1439502).
//  HEAD = /programs(MOMS_PROG_INFO) · ITEM = /grids(MOMS_GRID_OPT_INFO) · SUB = /columns(MOMS_GRID_COLUMN_INFO). 행 식별자는 모두 id.
//  저장은 행별 순차(새 행 POST · 기존 행 PUT /{id}), 실패하면 멈추고 앞서 반영된 건수를 오류 done 에 싣는다(콤보관리와 같은 규약).
//  플래그 칸(tool*·required·collective)은 boolean 으로 주고받는다 — 계약에 타입이 없어 기존 그리드 설정 API(/me/menus/{menu}/grids)와 같다고 본다.
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'
import { inOrder } from '@/utils/inOrder'
import { formatDateTime14 } from '@/utils/format/legacyDate'
import { HEAD, ITEM, MENU_CODE, SUB, type TableId } from '../consts'

export const BASE = '/api/v1/system/screen-settings'
const PATH: Record<TableId, string> = { [HEAD]: `${BASE}/programs`, [ITEM]: `${BASE}/grids`, [SUB]: `${BASE}/columns` }

/** 표 행 — 키는 1.0 SY03 COLUMN_CD(DB 컬럼명). 플래그 칸은 표 체크박스 셀 값('Y'/'N') */
type Stamp = { INS_DATE: string; INS_BY: string; UPD_DATE: string; UPD_BY: string }
export type HeadRow = { _id: number; SPR_NM: string; MENU_CD: string; MENU_NM: string; BUTTONS: string[] } & Stamp
export type ItemRow = { _id: number; SPR_NM: string; MENU_CD: string; GRID_CD: string; TOOL_ADD: YN; TOOL_RMV: YN; TOOL_IMP: YN; TOOL_EXP: YN } & Stamp
export type SubRow = { _id: number; SPR_NM: string; MENU_CD: string; GRID_CD: string; COLUMN_CD: string; COLUMN_REQ: YN; COLUMN_CLTV: YN; COLUMN_PRESET: string } & Stamp
export type YN = 'Y' | 'N'

export type HeadParams = { shipperCode?: string; menuCode?: string; columnCode?: string }
export type ItemParams = { shipperCode: string; menuCode: string }
export type SubParams = ItemParams & { gridCode: string }

type StampItem = { id: number; insertedAt?: string | null; insertedBy?: string | null; updatedAt?: string | null; updatedBy?: string | null }
type ProgramItem = StampItem & { shipperCode: string; menuCode: string; menuName?: string | null; buttons?: string[] | null }
type GridItem = StampItem & { shipperCode: string; menuCode: string; gridCode: string; toolAdd: boolean; toolRemove: boolean; toolUpload: boolean; toolDownload: boolean }
type ColumnItem = StampItem & { shipperCode: string; menuCode: string; gridCode: string; columnCode: string; required: boolean; collective: boolean; preset?: string | null }

const yn = (v: boolean): YN => (v ? 'Y' : 'N')
/** 일시는 DB 문자열 그대로 — 새 값 14자, 옛 1.0 값 18자(뒤 4자리는 초 아래). 둘 다 앞 14자로 표기한다(콤보관리와 같다) */
const fmtDate = (v?: string | null) => (v ? formatDateTime14(/^\d{18}$/.test(v) ? v.slice(0, 14) : v) : '')
const stamp = (i: StampItem): Stamp & { _id: number } => ({ _id: i.id, INS_DATE: fmtDate(i.insertedAt), INS_BY: i.insertedBy ?? '', UPD_DATE: fmtDate(i.updatedAt), UPD_BY: i.updatedBy ?? '' })

const toHead = (i: ProgramItem): HeadRow => ({ ...stamp(i), SPR_NM: i.shipperCode, MENU_CD: i.menuCode, MENU_NM: i.menuName ?? '', BUTTONS: i.buttons ?? [] })
const toItem = (i: GridItem): ItemRow => ({
  ...stamp(i),
  SPR_NM: i.shipperCode,
  MENU_CD: i.menuCode,
  GRID_CD: i.gridCode,
  TOOL_ADD: yn(i.toolAdd),
  TOOL_RMV: yn(i.toolRemove),
  TOOL_IMP: yn(i.toolUpload),
  TOOL_EXP: yn(i.toolDownload),
})
const toSub = (i: ColumnItem): SubRow => ({
  ...stamp(i),
  SPR_NM: i.shipperCode,
  MENU_CD: i.menuCode,
  GRID_CD: i.gridCode,
  COLUMN_CD: i.columnCode,
  COLUMN_REQ: yn(i.required),
  COLUMN_CLTV: yn(i.collective),
  COLUMN_PRESET: i.preset ?? '',
})

/** Query key — 맨 앞은 메뉴코드(탭 닫기 캐시 삭제). 저장·삭제 뒤 무효화는 all */
export const screenSettingKeys = {
  all: [MENU_CODE, 'screenSettings'] as const,
  list: (table: TableId, params: object | undefined) => [...screenSettingKeys.all, table, params] as const,
  codes: (kind: 'buttons' | 'columns', gridCode?: string) => [MENU_CODE, 'screenSettingCodes', kind, gridCode] as const,
}

const getItems = async <T>(url: string, params: object) => (await request.get<ApiResponseV2<{ items: T[] }>, object>(url, params)).data?.items ?? []

export const useGetHeads = (params: HeadParams): UseQueryResult<HeadRow[], Error> =>
  useQuery({ queryKey: screenSettingKeys.list(HEAD, params), queryFn: async () => (await getItems<ProgramItem>(PATH[HEAD], params)).map(toHead) })

/** params 가 없으면(위 행을 아직 안 골랐으면) 부르지 않는다 */
export const useGetItems = (params: ItemParams | undefined): UseQueryResult<ItemRow[], Error> =>
  useQuery({ queryKey: screenSettingKeys.list(ITEM, params), queryFn: async () => (await getItems<GridItem>(PATH[ITEM], params ?? {})).map(toItem), enabled: params != null })

export const useGetSubs = (params: SubParams | undefined): UseQueryResult<SubRow[], Error> =>
  useQuery({ queryKey: screenSettingKeys.list(SUB, params), queryFn: async () => (await getItems<ColumnItem>(PATH[SUB], params ?? {})).map(toSub), enabled: params != null })

/** HEAD 버튼 칸 선택지 — BE MenuAction 전부 */
export const useGetButtonCodes = (): UseQueryResult<string[], Error> =>
  useQuery({ queryKey: screenSettingKeys.codes('buttons'), queryFn: () => getItems<string>(`${BASE}/button-codes`, {}), staleTime: Infinity })

/** SUB 칼럼코드 칸 선택지 — 같은 GRID_CD 의 기존 COLUMN_CD(모든 화주 합) */
export const useGetColumnCodes = (gridCode: string | undefined): UseQueryResult<string[], Error> =>
  useQuery({ queryKey: screenSettingKeys.codes('columns', gridCode), queryFn: () => getItems<string>(`${BASE}/column-codes`, { gridCode }), enabled: !!gridCode })

/** HEAD 삭제 확인창 — 함께 지워지는 같은 화주·메뉴의 ITEM·SUB 건수 */
export const getDeleteImpact = async (id: number) => {
  const data = (await request.get<ApiResponseV2<{ gridCount: number; columnCount: number }>>(`${PATH[HEAD]}/${id}/delete-impact`)).data
  // 건수 없이 0건으로 보여 주면 연쇄 삭제를 숨기게 된다 — 확인창을 열지 않도록 실패로 돌린다
  if (data == null) throw new Error('delete-impact 응답에 건수가 없습니다.')
  return data
}

/** 저장 대상 행 — 표 편집본(키 = DB 컬럼명). _id 없음 = 새 행(POST) */
export type SaveRow = { table: TableId; row: Record<string, unknown> & { _id?: number } }

/** 저장 본문 — 앞뒤 공백은 잘라 보낸다(콤보관리와 같다). SUB 기존 행은 칼럼코드를 못 바꾼다(PUT 본문에 없다) */
const bodyOf = ({ table, row }: SaveRow): object => {
  const str = (k: string) => (typeof row[k] === 'string' ? (row[k] as string).trim() : '')
  const flag = (k: string) => row[k] === 'Y'
  if (table === HEAD) return { shipperCode: str('SPR_NM'), menuCode: str('MENU_CD'), menuName: str('MENU_NM'), buttons: Array.isArray(row.BUTTONS) ? row.BUTTONS : [] }
  const keys = { shipperCode: str('SPR_NM'), menuCode: str('MENU_CD'), gridCode: str('GRID_CD') }
  if (table === ITEM) return { ...keys, toolAdd: flag('TOOL_ADD'), toolRemove: flag('TOOL_RMV'), toolUpload: flag('TOOL_IMP'), toolDownload: flag('TOOL_EXP') }
  const flags = { required: flag('COLUMN_REQ'), collective: flag('COLUMN_CLTV'), preset: str('COLUMN_PRESET') || null }
  return row._id == null ? { ...keys, columnCode: str('COLUMN_CD'), ...flags } : flags
}

export const useSaveScreenSettings = (): UseMutationResult<number, Error, SaveRow[]> =>
  useMutation({
    mutationFn: (rows: SaveRow[]) =>
      inOrder(rows, (r) => (r.row._id == null ? request.post(PATH[r.table], bodyOf(r)) : request.put(`${PATH[r.table]}/${r.row._id}`, bodyOf(r)))),
  })

export const useDeleteScreenSettings = (): UseMutationResult<number, Error, { table: TableId; ids: number[] }> =>
  useMutation({ mutationFn: ({ table, ids }) => inOrder(ids, (id) => request.delete(PATH[table], id)) })
